import type { ColumnDef } from '@tanstack/react-table';
import { createSerialColumn, StackedCellText } from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import { EXPENSE_HEADS } from '../constants/zonalStatement.constants';
import type {
  ExpenseStatementDay,
  ExpenseTypeTotal,
  SpecimenStatementItem,
} from '../types/zonalStatement.types';
import { StatementAmountCell } from './StatementAmountCell';

export function getExpenseDayColumns(): ColumnDef<ExpenseStatementDay, unknown>[] {
  return [
    createSerialColumn<ExpenseStatementDay>(),
    {
      id: 'date',
      header: 'Date',
      cell: ({ row }) => formatDisplayDate(row.original.date),
    },
    ...EXPENSE_HEADS.map(
      (head): ColumnDef<ExpenseStatementDay, unknown> => ({
        id: head.key,
        header: head.label,
        cell: ({ row }) => <StatementAmountCell value={row.original[head.key]} />,
      }),
    ),
    {
      id: 'total',
      header: 'Total',
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">{formatNumber(row.original.total)}</span>
      ),
    },
  ];
}

export function getExpenseTypeColumns(): ColumnDef<ExpenseTypeTotal, unknown>[] {
  return [
    createSerialColumn<ExpenseTypeTotal>(),
    { id: 'name', header: 'Expense Type', cell: ({ row }) => row.original.name },
    {
      id: 'claimed',
      header: 'Claimed (Tk)',
      cell: ({ row }) => <StatementAmountCell value={row.original.claimed} />,
    },
    {
      id: 'approved',
      header: 'Approved (Tk)',
      cell: ({ row }) => <StatementAmountCell value={row.original.approved} />,
    },
  ];
}

export function getSpecimenStatementColumns(): ColumnDef<SpecimenStatementItem, unknown>[] {
  return [
    createSerialColumn<SpecimenStatementItem>(),
    {
      id: 'name',
      header: 'Specimen',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.name}
          secondary={row.original.code ?? <EmptyValue />}
        />
      ),
    },
    {
      id: 'bookType',
      header: 'Book Type',
      cell: ({ row }) => row.original.bookTypeName ?? <EmptyValue />,
    },
    {
      id: 'netPrice',
      header: 'Net Price',
      cell: ({ row }) => <StatementAmountCell value={row.original.netPrice} />,
    },
    {
      id: 'received',
      header: 'Received',
      cell: ({ row }) => <StatementAmountCell value={row.original.challanReceived} />,
    },
    {
      id: 'distributed',
      header: 'Distributed',
      cell: ({ row }) => <StatementAmountCell value={row.original.qty} />,
    },
    {
      id: 'value',
      header: 'Value (Tk)',
      cell: ({ row }) => <StatementAmountCell value={row.original.value} />,
    },
  ];
}
